import React, { useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import { useLiteratureReview } from "../../Context/LiteratureReviewContext";
import { updateLitreviewtitle } from "../../Utils/functions";
import "./LiteratureReviewLayout.css";
import { useLiteratureReviewReport } from "../../Context/LiteratureReviewReportContext";

const LiteratureReviewLayout = ({
  projectTitle = "Literature Review",
  editableTitle = false,
  steps = [],
  mainContent,
  sidebar,
  headerActions,
  onBack,
  onNext,
  backLabel = "",
  nextLabel = "Next",
  showBack = true,
  showNext = true,
  secondaryButtonLabel,
  onSecondaryClick,
  showSecondary = false,
}) => {
  const { id, setWorkflowData, navigateToStep } = useLiteratureReview();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [titleError, setTitleError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const reportNav = useLiteratureReviewReport();
  const showReturnToReport =
    reportNav?.hasSavedReport === true &&
    reportNav?.routeStep !== "research-report";

  const handleTitleEditStart = () => {
    setTitleDraft(projectTitle);
    setTitleError("");
    setIsEditingTitle(true);
  };

  const handleTitleCancel = () => {
    setIsEditingTitle(false);
    setTitleError("");
  };

  const handleTitleSave = async () => {
    const trimmed = titleDraft.trim();
    if (!trimmed) {
      setTitleError("Title cannot be empty.");
      return;
    }
    setIsSavingTitle(true);
    setTitleError("");
    try {
      const data = await updateLitreviewtitle(id, trimmed);
      setWorkflowData({ litreviewtitle: data.litreviewtitle });
      setSnackbar({ open: true, message: "Title updated", severity: "success" });
      setIsEditingTitle(false);
    } catch (err) {
      const status = err.status;
      if (status === 400) setTitleError("Title cannot be empty.");
      else if (status === 403) setTitleError("You don't have permission to edit this title.");
      else if (status === 404) setTitleError("Job not found. Please refresh.");
      else setTitleError("Failed to update title. Please try again.");
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === "Enter") handleTitleSave();
    if (e.key === "Escape") handleTitleCancel();
  };

  return (
    <div className="literature-review-layout">
      <div className="lr-header">
        <div className="lr-header-left">
          {editableTitle && isEditingTitle ? (
            <div className="lr-title-edit">
              <input
                className="lr-title-input"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                autoFocus
                disabled={isSavingTitle}
              />
              {titleError && <span className="lr-title-error">{titleError}</span>}
              <div className="lr-title-edit-actions">
                <button
                  className="lr-title-save-btn"
                  onClick={handleTitleSave}
                  disabled={isSavingTitle || !titleDraft.trim()}
                >
                  {isSavingTitle ? "Saving..." : "Save"}
                </button>
                <button
                  className="lr-title-cancel-btn"
                  onClick={handleTitleCancel}
                  disabled={isSavingTitle}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div
              className={`lr-project-title${editableTitle ? " lr-project-title--editable" : ""}`}
              onClick={editableTitle ? handleTitleEditStart : undefined}
              title={editableTitle ? "Click to edit title" : undefined}
            >
              {projectTitle}
              {editableTitle && (
                <span className="lr-title-edit-icon" aria-label="Edit title">✏️</span>
              )}
            </div>
          )}
          {showReturnToReport && (
            <button
              type="button"
              className="lr-nav-button lr-return-report-button"
              onClick={reportNav.goToReport}
            >
              Return to Report
            </button>
          )}
        </div>

        <div className="lr-header-center">
          {steps.length > 0 && (
            <div className="lr-progress-indicator">
              {steps.map((step, index) => (
                <React.Fragment key={step.id || index}>
                  <div
                    className={`lr-progress-dot ${step.isActive ? "active" : ""} ${step.isCompleted ? "completed" : ""} ${step.isCompleted ? "clickable" : ""}`}
                    title={step.label}
                    onClick={step.isCompleted ? () => navigateToStep(step.id) : undefined}
                  />
                  {index < steps.length - 1 && (
                    <div
                      className={`lr-progress-line ${step.isCompleted ? "completed" : ""}`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
          {steps.length > 0 && (
            <div className="lr-current-step">
              {steps.find((s) => s.isActive)?.label || ""}
            </div>
          )}
        </div>

        <div className="lr-header-right">
          {headerActions}
          {showBack && onBack && (
            <button className="lr-nav-button lr-back-button" onClick={onBack}>
              <span className="lr-back-arrow">&lt;</span> {backLabel}
            </button>
          )}
          {showNext && onNext && (
            <button className="lr-nav-button lr-next-button" onClick={onNext}>
              {nextLabel} &gt;&gt;
            </button>
          )}
          {showSecondary && secondaryButtonLabel && onSecondaryClick && (
            <button className="lr-nav-button lr-secondary-button" onClick={onSecondaryClick}>
              {secondaryButtonLabel} &gt;
            </button>
          )}
        </div>
      </div>

      <div className="lr-content-wrapper">
        <div className="lr-main-content">{mainContent}</div>
        {sidebar && <div className="lr-sidebar">{sidebar}</div>}
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default LiteratureReviewLayout;
